<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
	article: Object,
	section: Object,
	currentArticle: Object,
	user: Object,
	openArticle: Function,
	windowWidth: Number,
	swipedArticleId: String,
	setSwipedArticle: Function,
	filteredForSaved: Boolean,
	handleArticleAction: Function,
	gameMode: Boolean,
});

const translateX = ref(0);
const threshold = 50;
let startX = 0;

let notFiltered = computed(() => !props.filteredForSaved || props.user?.training?.[props.article.id]?.saved);

const isSwiped = computed(() => props.swipedArticleId === props.article.id);
const gameStatusIcon = computed(() => {
	const status = props.user?.training?.[props.article.id];
	if (status?.saved) return 'fa-solid fa-bookmark';
	if (status?.like) return 'fa-solid fa-thumbs-up';
	if (status?.dislike) return 'fa-solid fa-thumbs-down';
	return '';
});

watch(isSwiped, (newVal) => {
	if (!newVal) {
		translateX.value = 0;
	}
});

function onTouchStart(e) {
	startX = e.touches[0].clientX;
}

function onTouchMove(e) {
	const deltaX = e.touches[0].clientX - startX;
	const revealWidth = props.gameMode ? 232 : 200;
	if (!isSwiped.value && deltaX < 0) {
		translateX.value = Math.max(deltaX, -revealWidth);
	} else if (isSwiped.value) {
		translateX.value = Math.max(-deltaX, 0);
	}
}

function onTouchEnd() {
	const revealWidth = props.gameMode ? 232 : 200;
	if (translateX.value <= -threshold) {
		translateX.value = -revealWidth;
		props.setSwipedArticle(props.article.id);
	} else {
		translateX.value = 0;
		props.setSwipedArticle(null);
	}
}

function handleClick(event) {
	var hasURL = props.article.url ? true : false;
	if (isSwiped.value) {
		props.setSwipedArticle(null);

	} else if (event
						&& (event.which == 2 || event.button == 4 || event.button == 1) // middle-click
						|| (event.button == 0 && (event.ctrlKey|| event.metaKey)) // augmented click
			 			) {
		if (hasURL) {
			// open in background
			window.open(props?.article?.url, '_blank').focus();
			props.handleArticleAction('read', undefined, article.id);
		}
	} else if (event?.target?.tagName === 'I' && event.pointerType !== "touch") {
		props.setSwipedArticle(props.article.id);
		translateX.value = props.gameMode ? -232 : -200;

	} else {
		props.setSwipedArticle(null);
		props.openArticle(props.article.id, props.section, props.windowWidth < 768);
	}
}
</script>

<template>
	<div v-if="notFiltered"
		class="swipe-wrapper"
		:data-swiped="isSwiped"
		@touchstart.passive="onTouchStart"
		@touchmove.passive="onTouchMove"
		@touchend.passive="onTouchEnd"
	>
		<div class="swipe-actions">
			<button v-if="!gameMode" class="action-btn read" @click.stop="handleArticleAction('read', undefined, article.id)">
				<i :class="['fa-check', user?.training?.[article.id]?.read ? 'fa-solid' : 'fa-light']" aria-hidden="true"></i>
				<span class="label">
					{{ user?.training?.[article.id]?.read ? 'Read' : 'Unread' }}
				</span>
			</button>
			<template v-else>
				<button class="action-btn game-like" :aria-label="user?.training?.[article.id]?.like ? 'Remove like' : 'Like game'" @click.stop="handleArticleAction('like', undefined, article.id)"><i :class="['fa-thumbs-up', user?.training?.[article.id]?.like ? 'fa-solid' : 'fa-light']" aria-hidden="true"></i><span class="label">Like</span></button>
				<button class="action-btn game-dislike" :aria-label="user?.training?.[article.id]?.dislike ? 'Remove dislike' : 'Dislike game'" @click.stop="handleArticleAction('dislike', undefined, article.id)"><i :class="['fa-thumbs-down', user?.training?.[article.id]?.dislike ? 'fa-solid' : 'fa-light']" aria-hidden="true"></i><span class="label">Dislike</span></button>
			</template>
			<button class="action-btn saved" @click.stop="handleArticleAction('saved', undefined, article.id)">
				<i :class="['fa-bookmark', user?.training?.[article.id]?.saved ? 'fa-solid' : 'fa-regular']" aria-hidden="true"></i>
				<span class="label">
					{{ user?.training?.[article.id]?.saved ? 'Saved' : 'Save' }}
				</span>
			</button>
		</div>
		<div
			class="swipe-content category-list-item"
			:class="{
				active: article.id === currentArticle?.id,
				read: user?.training?.[article.id]?.read,
				like: user?.training?.[article.id]?.like,
				dislike: user?.training?.[article.id]?.dislike,
				saved: user?.training?.[article.id]?.saved,
				'game-mode': gameMode,
				swiped: swipedArticleId === article.id
			}"
			:style="{ transform: `translateX(${translateX}px)` }"
			tabindex="0"
			:data-id="article.id"
			:data-href="article.url"
			@click.stop="handleClick($event)"
		>
			<i v-if="gameMode" :class="[gameStatusIcon, 'status']" aria-hidden="true" @click.stop="handleClick($event)"></i>
			<i v-else class="fa-regular fa-check status" @click.stop="handleClick($event)"></i>
			<span>{{ article.title }}</span>
			<span class="source">{{ article.source }}</span>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.swipe-wrapper {
	position: relative;
	overflow: hidden;
	touch-action: pan-y;
	border-bottom: 1px solid var(--border);
}
.swipe-actions {
	position: absolute;
	inset: 0;
	left: auto;
	display: flex;
	justify-content: stretch;
	align-items: stretch;
	padding: 0;
	gap: 0;
}
.action-btn {
	background: var(--action-button-read);
	border: none;
	color: var(--text1);
	cursor: pointer;
	font-size: 16px;
	font-weight: 500;
	margin-left: 2px;

	display: inline-flex;
	flex-flow: column;
	align-items: center;
	justify-content: space-evenly;
	padding: 4px;
	width: 98px;

	&:last-child { background: var(--action-button-saved); }
	&.game-like, &.game-dislike { width: 64px; background: var(--action-button-read); }
	i {
		display:block;
		font-size: 24px;
		user-select: none;
		-webkit-user-select: none;
	}
	--action-button-read: #fffefa;
	--action-button-saved: #ffe79a;
	@media(prefers-color-scheme:dark) {
	--action-button-read: #5a7692;
	--action-button-saved: #445c74;
	}
}

.swipe-content,
.swipe-content:visited {
	background-color: var(--bg2);
	color: var(--text);
	cursor: pointer;
	padding: 10px;
	display: block;
	padding: 16px 16px;
	text-decoration: none;

	transition: all .3s;
	transform: translateX(0);

	overflow: hidden;
	height: auto;

	@media(pointer:fine) {
		&:hover,
		&:active,
		&:focus {
			background-color: var(--nav-hover);
		}
	}
	&.dislike {
		color: var(--text-disabled);
	}
	&.active,
	&.active:hover {
		color: var(--text);
		background-color: var(--nav-active);
		@media(prefers-color-scheme:dark) { color: #FFFFFF; }
	}
	span {
		font-weight: 500;
		display: block;
	}
	.source {
		font-size: 0.85em;
  	font-weight: 300;
  	letter-spacing: 0.6px;
  	opacity: 0.7;
	}
}

/* swipe content icons styles*/
i[class^='fa'].status {
	border-radius: 30px;
	display: inline-block;
	font-size: 1.17em;
	float: right;
	opacity: 0;
	margin: -2px -3px 0 0;
	padding: 5px 0;
	text-align: center;
	width: 28px;

	.active & {
		opacity: .4;
		&:hover { background-color: var(--bg1); }
	}
	.read &,
	.like &,
	.saved & {
		opacity: 1;
	}
	.dislike & { opacity: 1; }
	.game-mode.like &,
	.game-mode.dislike &,
	.game-mode.saved & { opacity: 1; }
	.active.saved &:hover {
		background-color: unset;
	}
	.saved &::before {
		content: '\f02e';
		font-weight: 900;
		margin-right: 1px;
	}
	@media(pointer:fine) {
		.swipe-content:hover &,
		.swipe-content:focus &,
		.swipe-content:active & {
			opacity: 1;
			&::before { content: '\f142'; }
		}
	}
	.swipe-content.swiped & {
		opacity: 1;
		&::before { content: '\f00d'; }
	}
}

/* duplicate link */
details + .duplicate-active-link {display:none;}
details:not([open]):has(.active) + .duplicate-active-link {display:block;}
</style>
